import { firestore } from '../firebaseConfig'; // Firestore setup
import {
  arrayUnion,
  arrayRemove,
  collection,
  doc,
  Timestamp,
  getDoc,
  query,
  where,
  documentId,
  getDocs,
  runTransaction,
  increment,
} from 'firebase/firestore'; // Firestore functions
import { enrichItemsWithUserData } from './course';
import { getAddItemStatsUpdates, getRemoveItemStatsUpdates } from './metadataStats';

/**
 * Adds a word to the user's learning list if they meet criteria (e.g., subscription).
 * Uses a Batched Write for atomic updates.
 * @param {string} userId - The ID of the user.
 * @param {string} wordId - The ID of the word to add.
 * @returns {Promise<{status: string, wordId: string}>} - Success status and word ID.
 * @throws {Error} - Throws specific errors for known issues (premium, not found, permission) or re-throws others.
 */
export const addWord = async (userId, wordId, isSubscribed) => {
  // Document References
  const userWordDocRef = doc(firestore, 'users', userId, 'user_words', wordId);
  const wordDataRef = doc(firestore, 'words', wordId);
  const userWordsIdsRef = doc(firestore, 'users', userId, 'metadata', 'user_words_ids');

  try {
    // 1. Fetch the main word data
    const wordSnapshot = await getDoc(wordDataRef);
    if (!wordSnapshot.exists()) {
      console.error(`Word ${wordId} not found in the main 'words' collection.`);
      throw new Error(`Word ${wordId} not found.`); // Specific error for not found
    }
    const wordData = wordSnapshot.data();
    const wordLevel = wordData.level;

    // 2. Check user subscription status

    // 3. Enforce subscription rules
    if (!isSubscribed && wordLevel > 0 && wordLevel < 5) {
      const error = new Error(`Premium required for Level ${wordLevel} word.`);
      error.code = 'premium-required'; // Custom code for UI handling
      error.level = wordLevel;
      throw error;
    }

    // 4. Transaction with existence check to prevent double-add and double metadata increment
    await runTransaction(firestore, async (transaction) => {
      const existingDoc = await transaction.get(userWordDocRef);
      if (existingDoc.exists()) return; // Already added, skip

      const curr_full_timestamp = Timestamp.now();
      const newUserWordData = {
        word_ref: wordDataRef,
        srs_stage: 0,
        correct: 0,
        wrong: 0,
        level: wordLevel,
        learned_at: curr_full_timestamp,
        next_review: curr_full_timestamp,
        skipped: false,
      };

      transaction.set(userWordDocRef, newUserWordData);
      transaction.set(userWordsIdsRef, { data: arrayUnion(wordId) }, { merge: true });
      getAddItemStatsUpdates(transaction, userId, 'words', wordLevel, 0, false);
    });

    return { status: 'fulfilled', wordId: wordId };
  } catch (error) {
    // Handle specific known errors
    if (error.code === 'premium-required') {
      throw error; // Re-throw the specific error
    }
    if (error.message.includes('not found')) {
      // Check for our 'not found' error
      throw error; // Re-throw not found error
    }
    if (error.code === 'permission-denied') {
      // Provide a more user-friendly message or custom error code
      const permissionError = new Error('Permission denied. Please ensure you are logged in and have verified your email if required.');
      permissionError.code = 'permission-denied-addword'; // More specific code if needed
      throw permissionError;
    }

    // Log and re-throw unexpected errors
    console.error(`Error adding word ${wordId} for user ${userId}:`, error);
    throw error; // Re-throw for the caller to handle
  }
};

/**
 * Deletes a word from the user's learning list atomically.
 * Removes the word document from the user_words subcollection and
 * removes the word ID from the user_words_ids array in the main user document.
 * Uses a Batched Write for atomic updates.
 * @param {string} userId - The ID of the user.
 * @param {string} wordId - The ID of the word to delete.
 * @returns {Promise<{status: string, wordId: string}>} - Success status and word ID.
 * @throws {Error} - Throws specific errors for known issues (e.g., permission) or re-throws others.
 */
export const deleteWord = async (userId, wordId) => {
  // Document References
  const userWordDocRef = doc(firestore, 'users', userId, 'user_words', wordId);
  const userWordsIdsRef = doc(firestore, 'users', userId, 'metadata', 'user_words_ids');

  try {
    // Transaction moves the read inside to prevent concurrent double-decrement
    await runTransaction(firestore, async (transaction) => {
      const wordDoc = await transaction.get(userWordDocRef);
      if (!wordDoc.exists()) return; // Already deleted

      const wordData = wordDoc.data();
      const level = wordData.level;
      const srsStage = wordData.srs_stage || 0;
      const skipped = wordData.skipped || false;
      const correctCount = wordData.correct || 0;
      const wrongCount = wordData.wrong || 0;

      transaction.delete(userWordDocRef);
      transaction.set(userWordsIdsRef, { data: arrayRemove(wordId) }, { merge: true });
      getRemoveItemStatsUpdates(transaction, userId, 'words', level, srsStage, skipped, correctCount, wrongCount);
    });

    return { status: 'fulfilled', wordId: wordId };
  } catch (error) {
    if (error.code === 'permission-denied') {
      const permissionError = new Error('Permission denied while trying to delete the word. Please ensure you are logged in and authorized.');
      permissionError.code = 'permission-denied-deleteword';
      throw permissionError;
    }
    console.error(`Error deleting word ${wordId} for user ${userId}:`, error);
    throw error; // Re-throw for the calling component to handle
  }
};

/**
 * Marks a word as skipped for a user. If the word hasn't been added yet,
 * it adds it first (with skipped: true) atomically using a Transaction.
 * @param {string} userId - The ID of the user.
 * @param {string} wordId - The ID of the word to skip.
 * @returns {Promise<{status: string, wordId: string, skipped: boolean}>} - Success status.
 * @throws {Error} - Throws specific errors for known issues (premium, not found, permission) or re-throws others.
 */
export const skipWord = async (userId, wordId, isSubscribed) => {
  // Document References
  const userWordDocRef = doc(firestore, 'users', userId, 'user_words', wordId);
  const wordDataRef = doc(firestore, 'words', wordId);
  const userWordsIdsRef = doc(firestore, 'users', userId, 'metadata', 'user_words_ids');

  try {
    // --- Pre-Transaction Checks --
    const mainWordSnapshot = await getDoc(wordDataRef);
    if (!mainWordSnapshot.exists()) {
      console.error(`Word ${wordId} not found in the main 'words' collection.`);
      throw new Error(`Word ${wordId} not found.`);
    }
    const mainWordData = mainWordSnapshot.data();
    const wordLevel = mainWordData.level;

    // 3. Enforce subscription rules (before starting transaction)
    if (!isSubscribed && wordLevel > 0 && wordLevel < 5) {
      const error = new Error(`Premium required to interact with Level ${wordLevel} word.`);
      error.code = 'premium-required';
      error.level = wordLevel;
      throw error;
    }
    // --- End Pre-Transaction Checks ---

    // --- Start Transaction ---
    const alreadySkipped = await runTransaction(firestore, async (transaction) => {
      const userWordDoc = await transaction.get(userWordDocRef);
      const curr_full_timestamp = Timestamp.now(); // Get timestamp inside transaction

      if (userWordDoc.exists()) {
        // --- Case 1: Word already exists in user's list ---
        const existingData = userWordDoc.data();
        if (existingData.skipped) return true; // Already skipped, no-op
        const currentSrsStage = existingData.srs_stage || 0;
        
        // Update metadata stats: move from current SRS stage to skipped
        const srsUpdate = {
          words: {
            [currentSrsStage]: increment(-1),
            skipped: increment(1)
          }
        };
        const srsRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_srs');
        transaction.set(srsRef, srsUpdate, { merge: true });
        
        // Update the existing document to mark as skipped.
        transaction.update(userWordDocRef, { skipped: true });
      } else {
        // --- Case 2: Word does NOT exist in user's list ---
        // Add the word, marked as skipped, and update the user's word list.

        // Define the data for the new document, setting skipped: true
        const newUserWordData = {
          word_ref: wordDataRef,
          srs_stage: 0, // Or specific stage if skipping means something else
          correct: 0,
          wrong: 0,
          learned_at: curr_full_timestamp, // Timestamp when interaction (skip) happened
          next_review: curr_full_timestamp, // Review time might be irrelevant if skipped
          skipped: true, // Set skipped to true directly
          level: wordLevel,
        };

        // Update metadata stats for new skipped word
        const levelUpdate = {
          words: {
            [wordLevel]: increment(1)
          }
        };
        const levelRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_level');
        transaction.set(levelRef, levelUpdate, { merge: true });
        
        const srsUpdate = {
          words: {
            skipped: increment(1)
          }
        };
        const srsRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_srs');
        transaction.set(srsRef, srsUpdate, { merge: true });
        
        // Queue the writes
        transaction.set(userWordDocRef, newUserWordData); // Set the new skipped word data
        transaction.set(
          userWordsIdsRef,
          {
            data: arrayUnion(wordId),
          },
          { merge: true }
        );
      }
    }); // --- End Transaction ---

    if (alreadySkipped) return { status: 'already_skipped', wordId };
    return { status: 'fulfilled', wordId: wordId, skipped: true };
  } catch (error) {
    // Handle specific known errors (some caught before transaction)
    if (error.code === 'premium-required') {
      throw error; // Re-throw the specific error
    }
    if (error.message.includes('not found')) {
      throw error; // Re-throw not found error
    }
    if (error.code === 'permission-denied') {
      const permissionError = new Error('Permission denied during skip operation. Please ensure you are logged in and authorized.');
      permissionError.code = 'permission-denied-skipword';
      throw permissionError;
    }

    // Log and re-throw unexpected errors (could be from transaction logic or retries)
    console.error(`Error skipping word ${wordId} for user ${userId}:`, error);
    throw error; // Re-throw for the caller
  }
};

/**
 * Unskips a word for a user by setting its 'skipped' field to false.
 * This operation is inherently atomic as it involves a single write.
 * @param {string} userId - The ID of the user.
 * @param {string} wordId - The ID of the word to unskip.
 * @returns {Promise<{status: string, wordId: string}>} - Success status and word ID.
 * @throws {Error} - Throws specific errors for known issues (e.g., permission, not found) or re-throws others.
 */
export const unskipWord = async (userId, wordId) => {
  // Document References
  const wordDocRef = doc(firestore, 'users', userId, 'user_words', wordId);

  try {
    // Use transaction to update both the word and metadata stats
    await runTransaction(firestore, async (transaction) => {
      const wordDoc = await transaction.get(wordDocRef);
      
      if (!wordDoc.exists()) {
        throw new Error(`Word ${wordId} not found in user's list.`);
      }
      
      const wordData = wordDoc.data();
      if (!wordData.skipped) return; // Already unskipped, no-op
      const srsStage = wordData.srs_stage || 0;

      // Update metadata stats: move from skipped back to original SRS stage
      const srsUpdate = {
        words: {
          skipped: increment(-1),
          [srsStage]: increment(1)
        }
      };
      const srsRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_srs');
      transaction.set(srsRef, srsUpdate, { merge: true });
      
      // Update the document, setting skipped to false
      transaction.update(wordDocRef, {
        skipped: false,
      });
    });

    return { status: 'fulfilled', wordId: wordId };
  } catch (error) {
    // Handle specific Firestore errors
    if (error.code === 'permission-denied') {
      const permissionError = new Error('Permission denied while trying to unskip the word.');
      permissionError.code = 'permission-denied-unskipword';
      throw permissionError;
    }
    if (error.code === 'not-found') {
      const notFoundError = new Error(`Cannot unskip word '${wordId}' because it was not found in the user's list.`);
      notFoundError.code = 'not-found-unskipword';
      throw notFoundError;
    }

    // Log and re-throw unexpected errors
    console.error(`Error unskipping word ${wordId} for user ${userId}:`, error);
    throw error; // Re-throw for the calling component to handle
  }
};

// Function to add multiple words using a Firestore batch write
export const addMultipleWordsBatch = async (userId, wordIdArray, isSubscribed) => {
  if (!userId) {
    throw new Error('User ID is required.');
  }
  const uniqueWordIds = Array.isArray(wordIdArray) ? [...new Set(wordIdArray)] : [];

  if (uniqueWordIds.length === 0) {
    return {
      addedCount: 0,
      skippedPremiumCount: 0,
      errorCount: 0,
      errors: [],
      addedWordIds: [],
    };
  }

  if (uniqueWordIds.length > 20) {
    throw new Error('Cannot add more than 20 words in a single batch operation.');
  }

  // --- Document References ---
  const userWordsRef = collection(firestore, 'users', userId, 'user_words');
  const userWordsIdsRef = doc(firestore, 'users', userId, 'metadata', 'user_words_ids');

  let skippedPremiumCount = 0;
  let errorCount = 0;
  const errors = [];

  try {
    // 1. Bulk-fetch public word data
    const wordsRef = collection(firestore, 'words');
    const MAX_IN = 30;
    const wordDataMap = new Map();

    for (let i = 0; i < uniqueWordIds.length; i += MAX_IN) {
      const chunk = uniqueWordIds.slice(i, i + MAX_IN);
      const q = query(wordsRef, where(documentId(), 'in', chunk));
      const snap = await getDocs(q);
      snap.forEach((docSnap) => {
        if (docSnap.exists()) wordDataMap.set(docSnap.id, docSnap.data());
      });
    }

    // 2. Determine eligible words (pass subscription check)
    const eligibleWords = [];
    uniqueWordIds.forEach((wordId) => {
      const data = wordDataMap.get(wordId);
      if (!data) { errors.push(`Word ${wordId} not found.`); errorCount++; return; }
      const lvl = data.level;
      if (!isSubscribed && lvl > 0 && lvl < 5) { skippedPremiumCount++; return; }
      eligibleWords.push({ wordId, lvl });
    });

    if (eligibleWords.length === 0) {
      return { addedCount: 0, skippedPremiumCount, errorCount, errors, addedWordIds: [] };
    }

    // 3. Transaction: read existing docs, write only truly new ones, aggregate metadata
    let addedWordIds = [];
    await runTransaction(firestore, async (transaction) => {
      addedWordIds = []; // reset on each retry

      // Read all user_word docs inside the transaction
      const userWordRefs = eligibleWords.map(({ wordId }) => doc(userWordsRef, wordId));
      const userWordDocs = await Promise.all(userWordRefs.map((ref) => transaction.get(ref)));

      // Abort entirely if any word is already in the user's list
      const alreadyExisting = eligibleWords.filter((_, i) => userWordDocs[i].exists());
      if (alreadyExisting.length > 0) {
        const alreadyExistingError = new Error('Some words are already in your list. No words were added.');
        alreadyExistingError.code = 'already-exists';
        alreadyExistingError.alreadyExisted = true;
        throw alreadyExistingError;
      }

      const newWords = eligibleWords;

      const now = Timestamp.now();

      // Aggregate level/srs increments — avoids duplicate writes to the same metadata doc
      // (last set() wins in a transaction; calling per-word would only apply the final word's increment)
      const levelIncrements = {}; // { [level]: count }
      let srsStage0Count = 0;

      newWords.forEach(({ wordId, lvl }) => {
        transaction.set(doc(userWordsRef, wordId), {
          word_ref: doc(wordsRef, wordId),
          srs_stage: 0,
          correct: 0,
          wrong: 0,
          learned_at: now,
          next_review: now,
          skipped: false,
          level: lvl,
        });
        levelIncrements[lvl] = (levelIncrements[lvl] || 0) + 1;
        srsStage0Count++;
        addedWordIds.push(wordId);
      });

      // Single write per metadata document
      const levelRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_level');
      const srsRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_srs');

      const levelUpdate = { words: {} };
      Object.entries(levelIncrements).forEach(([lvl, count]) => {
        levelUpdate.words[lvl] = increment(count);
      });
      transaction.set(levelRef, levelUpdate, { merge: true });
      transaction.set(srsRef, { words: { 0: increment(srsStage0Count) } }, { merge: true });

      transaction.set(userWordsIdsRef, { data: arrayUnion(...addedWordIds) }, { merge: true });
    });

    return {
      addedCount: addedWordIds.length,
      skippedPremiumCount,
      errorCount,
      errors,
      addedWordIds,
    };
  } catch (err) {
    console.error('Batch add failed:', err);
    if (err.code === 'already-exists') {
      return {
        addedCount: 0,
        skippedPremiumCount,
        errorCount: 0,
        errors: [err.message],
        addedWordIds: [],
        alreadyExisted: true,
      };
    }
    const msg =
      err.code === 'permission-denied'
        ? 'Permission denied during bulk add. Please check login and email verification.'
        : err.message || 'Unknown error during bulk add.';
    return {
      addedCount: 0,
      skippedPremiumCount: 0,
      errorCount: uniqueWordIds.length - skippedPremiumCount,
      errors: [msg, ...errors],
      addedWordIds: [],
    };
  }
};

/**
 * Fetches the complete details for a single word, combining public data
 * with user-specific progress data.
 * @param {string} wordId - The ID of the word to fetch.
 * @param {string} [userId] - The optional ID of the logged-in user.
 * @returns {Promise<object>} A complete word object.
 */
export const getWordDetails = async (wordId, userId) => {
  if (!wordId) throw new Error('Word ID is required.');

  // 1. Get the public base data for the word
  const wordDocRef = doc(firestore, 'words', wordId);
  const wordDocSnap = await getDoc(wordDocRef);

  if (!wordDocSnap.exists()) {
    throw new Error(`Word with ID ${wordId} not found.`);
  }

  const wordData = { id: wordId, ...wordDocSnap.data() };

  // 2. If a user is logged in, get their specific progress for this word
  if (userId) {
    const userWordDocRef = doc(firestore, 'users', userId, 'user_words', wordId);
    const userWordDocSnap = await getDoc(userWordDocRef);

    if (userWordDocSnap.exists()) {
      const userProgress = userWordDocSnap.data();
      // 3. Merge the two datasets, giving preference to user data
      return {
        ...wordData, // Base data
        ...userProgress, // User's SRS stage, next_review, etc.
        isLearned: true, // Mark as learned since a user document exists
      };
    }
  }

  // 4. If no user or no user progress, return the base data
  return {
    ...wordData,
    isLearned: false,
    srsStage: 0, // Default values
    skipped: false,
  };
};

/**
 * Fetches an array of full word objects based on an array of IDs and enriches them with user data.
 * @param {string[]} wordIds - An array of word IDs to fetch.
 * @param {string} [userId] - Optional user ID to add progress data like 'learned' and 'srs_stage'.
 * @returns {Promise<object[]>} An array of complete, enriched word objects.
 */
export const getMultipleWordDetails = async (wordIds, userId, learnedWordIds = null) => {
  if (!wordIds || wordIds.length === 0) return [];

  // 1. Fetch all the raw word documents concurrently.
  const wordPromises = wordIds.map((id) => getDoc(doc(firestore, 'words', id)));
  const wordDocsSnapshots = await Promise.all(wordPromises);

  const rawWords = wordDocsSnapshots
    .filter((snap) => snap.exists())
    .map((snap) => ({
      id: snap.id,
      ...snap.data(),
    }));

  // 2. Enrich the raw results with user-specific data in a single batch.
  return enrichItemsWithUserData(rawWords, userId, 'words', learnedWordIds);
};

const fetchExampleSentenceIds = async (wordId) => {
  if (!wordId) return [];
  const metadataRef = doc(firestore, `words/${wordId}/metadata/appears_in_sentences`);
  const metadataSnap = await getDoc(metadataRef);
  if (!metadataSnap.exists()) return [];
  return metadataSnap.data()?.data || [];
};

/**
 * Gets shuffled sentence IDs for a given word ID. Uses Fisher-Yates for an
 * unbiased shuffle so paginated displays don't favor the same examples.
 * @param {string} wordId - The ID of the word.
 * @returns {Promise<string[]>} An array of shuffled sentence IDs.
 */
export const getExampleSentenceIdsForWord = async (wordId) => {
  const sentenceIds = [...(await fetchExampleSentenceIds(wordId))];
  for (let i = sentenceIds.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [sentenceIds[i], sentenceIds[j]] = [sentenceIds[j], sentenceIds[i]];
  }
  return sentenceIds;
};

/**
 * Picks one example sentence ID for a given word. If a set of already-learned
 * sentence IDs is provided, prefers a sentence the user has already seen so
 * the "hear in context" example sits in familiar vocabulary; falls back to
 * any sentence if there's no overlap.
 * @param {string} wordId - The ID of the word.
 * @param {Set<string>} [learnedSentenceIds] - Optional set of sentence IDs the user has learned.
 * @returns {Promise<string|null>} A single sentence ID, or null if none exist.
 */
export const pickExampleSentenceIdForWord = async (wordId, learnedSentenceIds = null) => {
  const sentenceIds = await fetchExampleSentenceIds(wordId);
  if (sentenceIds.length === 0) return null;

  if (learnedSentenceIds && learnedSentenceIds.size > 0) {
    const known = sentenceIds.filter((id) => learnedSentenceIds.has(id));
    if (known.length > 0) return known[Math.floor(Math.random() * known.length)];
  }

  return sentenceIds[Math.floor(Math.random() * sentenceIds.length)];
};

/**
 * Fetches sentence data for specific sentence IDs.
 * @param {string[]} sentenceIds - Array of sentence IDs to fetch.
 * @param {string} [userId] - Optional user ID to add progress data like 'learned' and 'srs_stage'.
 * @returns {Promise<object[]>} An array of enriched sentence objects.
 */
export const getSentencesByIds = async (sentenceIds, userId, learnedSentenceIds = null) => {
  if (!sentenceIds || sentenceIds.length === 0) return [];

  // Fetch the full sentence data for the provided IDs.
  const sentencePromises = sentenceIds.map((id) => getDoc(doc(firestore, 'sentences', id)));
  const sentenceDocs = await Promise.all(sentencePromises);

  const rawSentences = sentenceDocs
    .filter((snap) => snap.exists())
    .map((snap) => ({
      id: snap.id,
      ...snap.data(),
    }));

  // Enrich the raw sentences with user-specific data.
  return enrichItemsWithUserData(rawSentences, userId, 'sentences', learnedSentenceIds);
};
